import MultiSelectedDashOuter, { IMultiSelectedDashOuterMenu } from './multi-selected-dash-outer';
import { reaction } from 'mobx';
import debounce from 'lodash/debounce';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { GroupUIEdge } from '@src/framework/editor/src/page/index/components/canvas/element-types/group-element/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { IMultiSelectedDashOuterManager } from '@src/slide/application/service/multi-selected-dash-outer/core/multi-selected-dash-outer-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';

/**
 * 目前的职责是管理多选时虚线框的渲染
 */
export default class MultiSelectedDashOuterManager implements IMultiSelectedDashOuterManager {
    public changeFloatState = debounce(() => {
        const array = this.selectionService.selectedElements.selectedArray;
        if (array && array.length > 1) {
            this.showOuter();
        } else {
            this.hideOuter();
        }
    }, 5, { maxWait: 10 });

    private menuOper: IMultiSelectedDashOuterMenu | null;
    private selectedArray: IElementBaseModel[] | null;
    private delayShowTimer;

    public constructor(
        private selectionService: ISelectionService,
        private editingStateManager: EditingStateManager,
    ) {
        this.menuOper = null;
        reaction(
            () => ({
                selectedLength: this.selectionService.selectedElements.length,
                curSlideChildrenLength: this.selectionService.currentSlide?.children?.length,
            }),
            () => {
                this.changeFloatState();
                this.removeObserverForModels(this.selectedArray);
                const { selectedArray } = this.selectionService.selectedElements;
                this.selectedArray = selectedArray ? selectedArray.slice(0, selectedArray.length) : null;
                this.addObserverForModels(this.selectedArray);
            },
        );

        reaction(
            () => this.editingStateManager.isInAnyEditingState(),
            (changing) => {
                if (changing) {
                    this.hideOuter();
                } else {
                    this.changeFloatState();
                }
            },
        );
    }

    private addObserverForModels(selectedArray: IElementBaseModel[] | null) {
        if (selectedArray) {
            selectedArray.forEach((model) => {
                model.changeSetEngine.addObserveForChangeAtPath('spPr.xfrm', this.changeFloatState);
            });
        }
    }

    private removeObserverForModels(selectedArray: IElementBaseModel[] | null) {
        if (selectedArray) {
            selectedArray.forEach((model) => {
                model.changeSetEngine.removeObserveForChangeAtPath('spPr.xfrm', this.changeFloatState);
            });
        }
    }

    // 算出外边框
    private calculateFrame(selectedElements: IElementBaseModel[]): IRect {
        const firstElementFrame = this.getRealFrame(selectedElements[0]);
        const firstElementAfterRotate = getFrameAfterRotate(firstElementFrame, firstElementFrame.rotate);
        let { left, top } = firstElementAfterRotate;
        let right = left + firstElementAfterRotate.width;
        let bottom = top + firstElementAfterRotate.height;
        selectedElements.slice(1).forEach((selectedElement) => {
            const frameAfterRotate = selectedElement.getFrame().outerFrameOnSlide();
            left = Math.min(left, frameAfterRotate.left);
            top = Math.min(top, frameAfterRotate.top);
            right = Math.max(right, frameAfterRotate.left + frameAfterRotate.width);
            bottom = Math.max(bottom, frameAfterRotate.top + frameAfterRotate.height);
        });
        const rect = {
            left,
            top,
            width: right - left,
            height: bottom - top,
        };
        return rect;
    }

    private getRealFrame(element: IElementBaseModel): IFrame {
        const frameOnSlide = element.getFrame?.().frameOnSlide();
        if (element.type === UIModelType.GROUP) {
            return {
                ...frameOnSlide,
                left: frameOnSlide.left - GroupUIEdge,
                top: frameOnSlide.top - GroupUIEdge,
                width: frameOnSlide.width + (2 * GroupUIEdge),
                height: frameOnSlide.height + (2 * GroupUIEdge),
            };
        }
        return frameOnSlide;
    }

    private showOuter() {
        const array = this.selectionService.selectedElements.selectedArray;
        if (array?.length && array?.length > 1) {
            if (this.delayShowTimer) {
                clearTimeout(this.delayShowTimer);
            }
            // 监听isInAnyEditingState的变更无法保证组合子元素的frame异步转换已完成
            // debounce和setTimeout两个宏任务则能确保frame已转换
            this.delayShowTimer = setTimeout(() => {
                if (array?.length && array?.length > 1) {
                    const rect = this.calculateFrame(array);
                    this.menuOper = MultiSelectedDashOuter.createMenu(rect);
                    this.menuOper.show(rect);
                } else {
                    // 避免其他操作清除了selectedArray导致crash
                    clearTimeout(this.delayShowTimer);
                }
            });
        }
    }

    private hideOuter() {
        if (this.delayShowTimer) {
            clearTimeout(this.delayShowTimer);
        }
        if (this.menuOper) {
            this.menuOper.hide();
        }
    }
}
